import {
    Add,
    Close,
    Edit,
    Delete,
    ArrowDownward,
    ArrowUpward,
    ExpandMore,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Modal,
    Select,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import "./Forms.css";

import ArrowOnboarding from "../static/ic_arrow.png";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";

const ToolsPage = () => {
    const backendEndpoint = process.env.REACT_APP_BACKEND_ENDPOINT;

    const [openToolModal, setOpenToolModal] = useState(false);
    const [openFixParamModal, setOpenFixParamModal] = useState(false);
    const [openDynamicParamModal, setOpenDynamicParamModal] = useState(false);

    const [tools, setTools] = useState([]);
    const inputRef = useRef(null);
    const [deleteId, setDeleteId] = useState(null);

    const [validateTool, setValidateTool] = useState(false);
    const [toolId, setToolId] = useState(null);
    const [toolName, setToolName] = useState("");
    const [toolDescription, setToolDescription] = useState("");
    const [toolUrl, setToolUrl] = useState("");
    const [toolMethod, setToolMethod] = useState("get");
    const [toolFixParams, setToolFixParams] = useState([]);
    const [toolDynamicParams, setToolDynamicParams] = useState([]);
    const [toolCustomJsonBody, setToolCustomJsonBody] = useState(null);

    const isToolJsonBodyValid = useMemo(() => {
        try {
            JSON.parse(toolCustomJsonBody.replace(/{{.*}}/g, "1"));
            return true;
        } catch (e) {
            return false;
        }
    }, [toolCustomJsonBody]);

    const [validateFixParam, setValidateFixParam] = useState(false);
    const [fixParamIdx, setFixParamIdx] = useState(null);
    const [fixParamName, setFixParamName] = useState("");
    const [fixParamDescription, setFixParamDescription] = useState("");
    const [fixParamValue, setFixParamValue] = useState("");
    const [fixParamType, setFixParamType] = useState("string");
    const [fixParamLocation, setFixParamLocation] = useState("json_body");

    const [validateDynamicParam, setValidateDynamicParam] = useState(false);
    const [dynamicParamIdx, setDynamicParamIdx] = useState(null);
    const [dynamicParamName, setDynamicParamName] = useState("");
    const [dynamicParamDescription, setDynamicParamDescription] = useState("");
    const [dynamicParamType, setDynamicParamType] = useState("string");
    const [dynamicParamLocation, setDynamicParamLocation] =
        useState("json_body");
    const [dynamicParamRequired, setDynamicParamRequired] = useState(true);

    const getCookieValue = useCallback(
        (name) =>
            document.cookie
                .match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)")
                ?.pop() || "",
        [],
    );

    const handleOnLoad = useCallback(async () => {
        const response = await fetch(`${backendEndpoint}/tools/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const { tools } = await response.json();
        setTools(tools);
    }, [backendEndpoint, getCookieValue]);

    const handleImportTools = (e) => {
        const file = e.target.files[0];
        inputRef.current.value = "";

        if (file.type !== "application/json") {
            return;
        }

        const reader = new FileReader();

        reader.onload = async (e) => {
            const fileContent = e.target.result;
            const toolsToImport = JSON.parse(fileContent);

            const response = await fetch(`${backendEndpoint}/tools/import/`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify({
                    tools: toolsToImport,
                }),
            });

            const { importedTools } = await response.json();

            setTools((prev) => [...prev, ...importedTools]);
        };

        reader.readAsText(file);
    };

    const handleExportTools = async () => {
        const response = await fetch(`${backendEndpoint}/tools/export/`, {
            method: "GET",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        const { exportedTools } = await response.json();

        const blob = new Blob([JSON.stringify(exportedTools, null, 2)], {
            type: "application/json",
        });

        const elem = document.createElement("a");
        elem.download = "chiko-tools.json";
        elem.href = URL.createObjectURL(blob);

        elem.click();
    };

    const handleOpenToolModal = (isNew = true) => {
        if (isNew) {
            setToolId(null);
            setToolName("");
            setToolDescription("");
            setToolUrl("");
            setToolMethod("get");
            setToolCustomJsonBody(null);
            setToolFixParams([]);
            setToolDynamicParams([]);
        }

        setValidateTool(false);
        setOpenToolModal(true);
    };

    const handleCloseToolModal = useCallback(() => {
        setOpenDynamicParamModal(false);
        setOpenFixParamModal(false);
        setOpenToolModal(false);
    }, []);

    const handleEditTool = (id) => {
        const tool = tools.find((tool) => tool.id === id);

        setToolId(tool.id);
        setToolName(tool.name);
        setToolDescription(tool.description);
        setToolUrl(tool.url);
        setToolMethod(tool.method);
        setToolFixParams(tool.fixParams);
        setToolDynamicParams(tool.dynamicParams);
        setToolCustomJsonBody(tool.customJsonBody);

        handleOpenToolModal(false);
    };

    const handleAddTool = useCallback(
        async (newTool) => {
            const response = await fetch(`${backendEndpoint}/tools/`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": getCookieValue("csrfToken"),
                },
                body: JSON.stringify(newTool),
            });

            const addedTool = await response.json();

            setTools((prev) => [...prev, addedTool]);
        },
        [backendEndpoint, getCookieValue],
    );

    const handleUpdateTool = useCallback(
        async (newTool) => {
            const response = await fetch(
                `${backendEndpoint}/tools/${toolId}/`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": getCookieValue("csrfToken"),
                    },
                    body: JSON.stringify(newTool),
                },
            );

            const updatedTool = await response.json();

            setTools((prev) =>
                prev.map((tool) => (tool.id !== toolId ? tool : updatedTool)),
            );
        },
        [toolId, backendEndpoint, getCookieValue],
    );

    const handleDeleteTool = async () => {
        if (deleteId === null) return;

        await fetch(`${backendEndpoint}/tools/${deleteId}/`, {
            method: "DELETE",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": getCookieValue("csrfToken"),
            },
        });

        setTools((prev) => prev.filter((tool) => tool.id !== deleteId));
        setDeleteId(null);
    };

    const handleToolOk = useCallback(async () => {
        setValidateTool(true);

        if (
            toolName === "" ||
            toolDescription === "" ||
            toolUrl === "" ||
            toolMethod === "" ||
            (toolCustomJsonBody !== null && !isToolJsonBodyValid)
        ) {
            return;
        }

        const newTool = {
            name: toolName,
            description: toolDescription,
            url: toolUrl,
            method: toolMethod,
            customJsonBody: toolCustomJsonBody,
            fixParams: toolFixParams,
            dynamicParams: toolDynamicParams,
        };

        if (toolId === null) {
            handleAddTool(newTool);
        } else {
            handleUpdateTool(newTool);
        }

        handleCloseToolModal();
    }, [
        toolId,
        toolName,
        toolDescription,
        toolUrl,
        toolMethod,
        toolCustomJsonBody,
        isToolJsonBodyValid,
        toolFixParams,
        toolDynamicParams,
        handleAddTool,
        handleUpdateTool,
        handleCloseToolModal,
    ]);

    const handleOpenFixParamModal = (isNew = true) => {
        if (isNew) {
            setFixParamIdx(null);
            setFixParamName("");
            setFixParamDescription("");
            setFixParamValue("");
            setFixParamType("string");
            setFixParamLocation("json_body");
        }

        setValidateFixParam(false);
        setOpenFixParamModal(true);
    };

    const handleCloseFixParamModal = () => {
        setOpenFixParamModal(false);
    };

    const handleEditFixParam = (idx) => {
        const param = toolFixParams.find((_, paramIdx) => paramIdx === idx);

        setFixParamIdx(idx);
        setFixParamName(param.name);
        setFixParamDescription(param.description);
        setFixParamValue(param.value);
        setFixParamType(param.type);
        setFixParamLocation(param.location);

        handleOpenFixParamModal(false);
    };

    const handleFixParamOk = () => {
        setValidateFixParam(true);

        if (
            fixParamName === "" ||
            fixParamDescription === "" ||
            fixParamValue === "" ||
            fixParamType === "" ||
            fixParamLocation === ""
        ) {
            return;
        }

        const newFixParam = {
            name: fixParamName,
            description: fixParamDescription,
            value: fixParamValue,
            type: fixParamType,
            location: fixParamLocation,
        };

        if (fixParamIdx === null) {
            handleAddToolFixParam(newFixParam);
        } else {
            handleUpdateToolFixParam(newFixParam);
        }

        handleCloseFixParamModal();
    };

    const handleAddToolFixParam = (newFixParam) => {
        setToolFixParams((prev) => [
            ...prev,
            {
                id: null,
                ...newFixParam,
            },
        ]);
    };

    const handleUpdateToolFixParam = (newFixParam) => {
        setToolFixParams((prev) =>
            prev.map((param, paramIdx) =>
                paramIdx !== fixParamIdx
                    ? param
                    : {
                          id: param.id,
                          ...newFixParam,
                      },
            ),
        );
    };

    const handleDeleteToolFixParam = (idx) => {
        setToolFixParams((prev) =>
            prev.filter((_, paramIdx) => paramIdx !== idx),
        );
    };

    const handleOpenDynamicParamModal = (isNew = true) => {
        if (isNew) {
            setDynamicParamIdx(null);
            setDynamicParamName("");
            setDynamicParamDescription("");
            setDynamicParamType("string");
            setDynamicParamLocation("json_body");
            setDynamicParamRequired(true);
        }

        setValidateDynamicParam(false);
        setOpenDynamicParamModal(true);
    };

    const handleCloseDynamicParamModal = () => {
        setOpenDynamicParamModal(false);
    };

    const handleEditDynamicParam = (idx) => {
        const param = toolDynamicParams.find((_, paramIdx) => paramIdx === idx);

        setDynamicParamIdx(idx);
        setDynamicParamName(param.name);
        setDynamicParamDescription(param.description);
        setDynamicParamType(param.type);
        setDynamicParamLocation(param.location);
        setDynamicParamRequired(param.required);

        handleOpenDynamicParamModal(false);
    };

    const handleDynamicParamOk = () => {
        setValidateDynamicParam(true);

        if (
            dynamicParamName === "" ||
            dynamicParamDescription === "" ||
            dynamicParamType === "" ||
            dynamicParamLocation === "" ||
            dynamicParamRequired === ""
        ) {
            return;
        }

        const newDynamicParam = {
            name: dynamicParamName,
            description: dynamicParamDescription,
            type: dynamicParamType,
            location: dynamicParamLocation,
            required: dynamicParamRequired,
        };

        if (dynamicParamIdx === null) {
            handleAddToolDynamicParam(newDynamicParam);
        } else {
            handleUpdateToolDynamicParam(newDynamicParam);
        }

        handleCloseDynamicParamModal();
    };

    const handleAddToolDynamicParam = (newDynamicParam) => {
        setToolDynamicParams((prev) => [
            ...prev,
            {
                id: null,
                ...newDynamicParam,
            },
        ]);
    };

    const handleUpdateToolDynamicParam = (newDynamicParam) => {
        setToolDynamicParams((prev) =>
            prev.map((param, paramIdx) =>
                paramIdx !== dynamicParamIdx
                    ? param
                    : {
                          id: param.id,
                          ...newDynamicParam,
                      },
            ),
        );
    };

    const handleDeleteToolDynamicParam = (idx) => {
        setToolDynamicParams((prev) =>
            prev.filter((_, paramIdx) => paramIdx !== idx),
        );
    };

    useEffect(() => {
        handleOnLoad();
    }, [handleOnLoad]);

    return (
        <Box sx={{ width: "100%" }} id="integrations-container">
            <Box
                variant="h4"
                sx={{
                    width: "150",
                    color: "#333",
                    fontSize: "24px",
                    fontWeight: "500",
                    mb: 6,
                    padding: "10px 18px",
                }}
            >
                Chiko Connect
                <Box
                    variant="div"
                    sx={{
                        fontSize: "13px",
                        fontWeight: 400,
                        pr: "10px",
                    }}
                >
                    Conecte o Chiko a qualquer API para torná-lo ainda mais
                    autônomo e seguro
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    position: "absolute",
                    top: "32px",
                    right: "40px",
                }}
            >
                <Box>
                    <input
                        ref={inputRef}
                        hidden
                        type="file"
                        accept=".json"
                        onChange={handleImportTools}
                    />
                    <Button
                        id="import-tool-btn"
                        variant="outlined"
                        startIcon={<ArrowDownward />}
                        onClick={() => inputRef.current.click()}
                        sx={{
                            marginBottom: 2,
                            borderRadius: "25px",
                            padding: "8px 16px 6px",
                            textTransform: "none",
                            mr: 2,
                        }}
                    >
                        Importar
                    </Button>
                </Box>
                <Button
                    id="export-tool-btn"
                    variant="outlined"
                    startIcon={<ArrowUpward />}
                    onClick={handleExportTools}
                    sx={{
                        marginBottom: 2,
                        borderRadius: "25px",
                        padding: "8px 16px 6px",
                        textTransform: "none",
                        mr: 2,
                    }}
                >
                    Exportar
                </Button>
                <Button
                    id="add-tool-btn"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenToolModal}
                    sx={{
                        marginBottom: 2,
                        borderRadius: "25px",
                        padding: "8px 16px 6px",
                        textTransform: "none",
                    }}
                >
                    Adicionar
                </Button>
            </Box>

            {tools.length === 0 ? (
                <>
                    <Box
                        sx={{
                            position: "Absolute",
                            top: "100px",
                            right: "100px",
                            display: "block",
                        }}
                        id="divOnboardingConteudo"
                    >
                        Vamos conectar o Chiko às APIs? Clique aqui &nbsp;&nbsp;
                        <img
                            src={ArrowOnboarding}
                            alt="Adicionar Ferramenta"
                            height="80"
                        />
                    </Box>
                </>
            ) : (
                <List
                    id="contents-list"
                    sx={{
                        width: "100%",
                        // backgroundColor: "#eeeeee",
                        // height: 400,
                        borderRadius: 1,
                        overflowY: "auto",
                        border: "1px solid #eeeeee",
                        paddingTop: "0",
                        paddingBottom: "0",
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: "#E2E8F0",
                            padding: "14px",
                            fontWeight: "500",
                        }}
                    >
                        Ferramentas adicionadas
                    </Box>

                    {tools.map((tool) => (
                        <ListItem
                            key={tool.id}
                            onClick={() => handleEditTool(tool.id)}
                            sx={{
                                borderBottom: "1px solid #E2E8F0",
                                display: "flex",
                                justifyContent: "space-between",
                                ":hover": {
                                    backgroundColor: "#f8f3f3ff",
                                    cursor: "pointer",
                                    color: "#000",
                                },
                            }}
                        >
                            <ListItemText
                                primary={tool.name}
                                sx={{
                                    color: "#263145",
                                    fontSize: "11px !important",
                                }}
                            />
                            <Box>
                                {/*
                            <IconButton
                                edge="end"
                                aria-label="edit"
                                onClick={() => handleEditTool(tool.id)}
                            >
                                <Edit />
                            </IconButton>
                            */}
                                <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteId(tool.id);
                                    }}
                                >
                                    <Delete />
                                </IconButton>
                            </Box>
                        </ListItem>
                    ))}
                </List>
            )}

            <ConfirmDeleteModal
                open={deleteId !== null}
                onClose={() => {
                    setDeleteId(null);
                }}
                onConfirm={() => handleDeleteTool()}
            />

            {/* Tool Modal */}
            <Modal
                open={openToolModal}
                // onClose={handleCloseToolModal}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 2,
                }}
            >
                <Box
                    id="contents-modal-container"
                    className="modalInterno"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 800,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        position: "relative",
                        overflowX: "hidden",
                        overflowY: "auto",
                    }}
                >
                    <Box className="formHeader">
                        <Box className="formTitle">
                            {toolId === null
                                ? "Nova ferramenta"
                                : "Editar ferramenta"}
                        </Box>

                        <IconButton
                            edge="end"
                            aria-label="close"
                            onClick={handleCloseToolModal}
                            sx={{
                                position: "absolute",
                                top: 6,
                                right: 18,
                            }}
                        >
                            <Close />
                        </IconButton>
                    </Box>

                    <Box className="formContent">
                        <Box className="formLabel">Nome*</Box>

                        <TextField
                            value={toolName}
                            onChange={(e) => setToolName(e.target.value)}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            inputProps={{ maxLength: 64 }}
                            error={
                                validateTool && toolName === "" ? true : false
                            }
                            helperText={
                                validateTool && toolName === ""
                                    ? "Preencha o nome"
                                    : ""
                            }
                            className="inputs"
                        />

                        <Box className="formLabel">Descrição*</Box>

                        <TextField
                            value={toolDescription}
                            onChange={(e) => setToolDescription(e.target.value)}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            error={
                                validateTool && toolDescription === ""
                                    ? true
                                    : false
                            }
                            helperText={
                                validateTool && toolDescription === ""
                                    ? "Preencha a descrição"
                                    : ""
                            }
                            className="inputs"
                        />

                        <Box className="formLabel">URL*</Box>

                        <TextField
                            value={toolUrl}
                            onChange={(e) => setToolUrl(e.target.value)}
                            variant="outlined"
                            fullWidth
                            margin="dense"
                            error={
                                validateTool && toolUrl === "" ? true : false
                            }
                            helperText={
                                validateTool && toolUrl === ""
                                    ? "Preencha a URL"
                                    : ""
                            }
                            className="inputs"
                        />

                        <Box className="formLabel">Método HTTP*</Box>

                        <FormControl
                            fullWidth
                            sx={{
                                mt: "8px",
                                mb: "4px",
                            }}
                        >
                            <Select
                                value={toolMethod}
                                onChange={(e) => setToolMethod(e.target.value)}
                                className="selects"
                            >
                                <MenuItem value="get">GET</MenuItem>
                                <MenuItem value="post">POST</MenuItem>
                                <MenuItem value="put">PUT</MenuItem>
                                <MenuItem value="patch">PATCH</MenuItem>
                                <MenuItem value="delete">DELETE</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider
                            sx={{
                                mt: "20px",
                                mb: "10px",
                            }}
                        >
                            <Chip variant="outlined" label="Parâmetros fixos" />
                        </Divider>

                        <List
                            id="tool-fix-params-list"
                            sx={{
                                width: "100%",
                                backgroundColor: "#eeeeee",
                                borderRadius: 2,
                                overflowY: "auto",
                                mt: 2,
                            }}
                        >
                            {toolFixParams.length === 0 ? (
                                <Typography
                                    variant="h6"
                                    sx={{ textAlign: "center" }}
                                >
                                    Nenhum parametro fixo adicionado...
                                </Typography>
                            ) : null}
                            {toolFixParams.map((toolFixParam, paramIdx) => (
                                <ListItem
                                    key={paramIdx}
                                    sx={{
                                        borderBottom: "1px solid #ccc",
                                        display: "flex",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <ListItemText primary={toolFixParam.name} />
                                    <Box>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() =>
                                                handleEditFixParam(paramIdx)
                                            }
                                        >
                                            <Edit />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() =>
                                                handleDeleteToolFixParam(
                                                    paramIdx,
                                                )
                                            }
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                id="add-tool-fix-param-btn"
                                startIcon={<Add />}
                                onClick={handleOpenFixParamModal}
                                sx={{ marginBottom: 2, mt: 1 }}
                            >
                                Adicionar
                            </Button>
                        </Box>

                        <Divider
                            sx={{
                                mt: "20px",
                                mb: "10px",
                            }}
                        >
                            <Chip
                                variant="outlined"
                                label="Parâmetros dinâmicos"
                            />
                        </Divider>

                        <List
                            id="tool-dynamic-params-list"
                            sx={{
                                width: "100%",
                                backgroundColor: "#eeeeee",
                                borderRadius: 2,
                                overflowY: "auto",
                                mt: 2,
                            }}
                        >
                            {toolDynamicParams.length === 0 ? (
                                <Typography
                                    variant="h6"
                                    sx={{ textAlign: "center" }}
                                >
                                    Nenhum parametro dinâmico adicionado...
                                </Typography>
                            ) : null}
                            {toolDynamicParams.map(
                                (toolDynamicParam, paramIdx) => (
                                    <ListItem
                                        key={paramIdx}
                                        sx={{
                                            borderBottom: "1px solid #ccc",
                                            display: "flex",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <ListItemText
                                            primary={toolDynamicParam.name}
                                        />
                                        <Box>
                                            <IconButton
                                                edge="end"
                                                aria-label="edit"
                                                onClick={() =>
                                                    handleEditDynamicParam(
                                                        paramIdx,
                                                    )
                                                }
                                            >
                                                <Edit />
                                            </IconButton>
                                            <IconButton
                                                edge="end"
                                                aria-label="delete"
                                                onClick={() =>
                                                    handleDeleteToolDynamicParam(
                                                        paramIdx,
                                                    )
                                                }
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                ),
                            )}
                        </List>

                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Button
                                id="add-tool-dynamic-param-btn"
                                startIcon={<Add />}
                                onClick={handleOpenDynamicParamModal}
                                sx={{ marginBottom: 2, mt: 1 }}
                            >
                                Adicionar
                            </Button>
                        </Box>

                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMore />}
                                aria-controls="tools-advanced-settings-content"
                                id="tools-advanced-settings-header"
                            >
                                <Typography component="span">
                                    Avançado
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormGroup>
                                    <FormControlLabel
                                        label="Corpo JSON personalizado"
                                        sx={{ mt: 2 }}
                                        control={
                                            <Switch
                                                checked={
                                                    toolCustomJsonBody !== null
                                                }
                                                onChange={(e) =>
                                                    setToolCustomJsonBody(
                                                        e.target.checked
                                                            ? ""
                                                            : null,
                                                    )
                                                }
                                            />
                                        }
                                    />
                                </FormGroup>

                                {toolCustomJsonBody !== null && (
                                    <TextField
                                        value={toolCustomJsonBody}
                                        onChange={(e) =>
                                            setToolCustomJsonBody(
                                                e.target.value,
                                            )
                                        }
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        multiline
                                        minRows={5}
                                        maxRows={15}
                                        error={
                                            validateTool && !isToolJsonBodyValid
                                                ? true
                                                : false
                                        }
                                        helperText={
                                            validateTool && !isToolJsonBodyValid
                                                ? "Defina um JSON válido"
                                                : ""
                                        }
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                id="create-tool-btn"
                                onClick={handleToolOk}
                                variant="contained"
                                sx={{
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    marginTop: 2,
                                }}
                            >
                                Salvar
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            {/* Fix Param Modal */}
            <Modal
                open={openFixParamModal}
                // onClose={handleCloseFixParamModal}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                }}
            >
                <Box
                    id="tool-fix-param-modal-container"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 750,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "auto",
                    }}
                >
                    <IconButton
                        edge="end"
                        aria-label="close"
                        onClick={handleCloseFixParamModal}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6">Nome*</Typography>

                    <TextField
                        value={fixParamName}
                        onChange={(e) => setFixParamName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                            validateFixParam && fixParamName === ""
                                ? true
                                : false
                        }
                        helperText={
                            validateFixParam && fixParamName === ""
                                ? "Preencha o nome"
                                : ""
                        }
                    />

                    <Typography variant="h6">Descrição*</Typography>

                    <TextField
                        value={fixParamDescription}
                        onChange={(e) => setFixParamDescription(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                            validateFixParam && fixParamDescription === ""
                                ? true
                                : false
                        }
                        helperText={
                            validateFixParam && fixParamDescription === ""
                                ? "Preencha a descrição"
                                : ""
                        }
                    />

                    <Typography variant="h6">Valor*</Typography>

                    <TextField
                        value={fixParamValue}
                        onChange={(e) => setFixParamValue(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                            validateFixParam && fixParamValue === ""
                                ? true
                                : false
                        }
                        helperText={
                            validateFixParam && fixParamValue === ""
                                ? "Preencha o valor"
                                : ""
                        }
                    />

                    <Typography variant="h6">Tipo*</Typography>

                    <FormControl
                        fullWidth
                        sx={{
                            mt: "8px",
                            mb: "4px",
                        }}
                    >
                        <Select
                            value={fixParamType}
                            onChange={(e) => setFixParamType(e.target.value)}
                        >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="boolean">Booleano</MenuItem>
                            <MenuItem value="integer">Inteiro</MenuItem>
                            <MenuItem value="float">Float</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="h6">Local*</Typography>

                    <FormControl
                        fullWidth
                        sx={{
                            mt: "8px",
                            mb: "4px",
                        }}
                    >
                        <Select
                            value={fixParamLocation}
                            onChange={(e) =>
                                setFixParamLocation(e.target.value)
                            }
                        >
                            <MenuItem value="header">Header</MenuItem>
                            <MenuItem value="path">URL</MenuItem>
                            <MenuItem value="query">Query</MenuItem>
                            <MenuItem value="json_body">JSON</MenuItem>
                            <MenuItem value="form_body">Form</MenuItem>
                        </Select>
                    </FormControl>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Button
                            id="create-tool-fix-param-btn"
                            onClick={handleFixParamOk}
                            variant="contained"
                            sx={{
                                marginLeft: "auto",
                                marginRight: "auto",
                                marginTop: 2,
                            }}
                        >
                            OK
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Dynamic Param Modal */}
            <Modal
                open={openDynamicParamModal}
                // onClose={handleCloseDynamicParamModal}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 4,
                }}
            >
                <Box
                    id="tool-dynamic-param-modal-container"
                    sx={{
                        maxHeight: "100%",
                        width: "100%",
                        maxWidth: 750,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 4,
                        position: "relative",
                        overflow: "auto",
                    }}
                >
                    <IconButton
                        edge="end"
                        aria-label="close"
                        onClick={handleCloseDynamicParamModal}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 20,
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Typography variant="h6">Nome*</Typography>

                    <TextField
                        value={dynamicParamName}
                        onChange={(e) => setDynamicParamName(e.target.value)}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                            validateDynamicParam && dynamicParamName === ""
                                ? true
                                : false
                        }
                        helperText={
                            validateDynamicParam && dynamicParamName === ""
                                ? "Preencha o nome"
                                : ""
                        }
                    />

                    <Typography variant="h6">Descrição*</Typography>

                    <TextField
                        value={dynamicParamDescription}
                        onChange={(e) =>
                            setDynamicParamDescription(e.target.value)
                        }
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        error={
                            validateDynamicParam &&
                            dynamicParamDescription === ""
                                ? true
                                : false
                        }
                        helperText={
                            validateDynamicParam &&
                            dynamicParamDescription === ""
                                ? "Preencha a descrição"
                                : ""
                        }
                    />

                    <Typography variant="h6">Tipo*</Typography>

                    <FormControl
                        fullWidth
                        sx={{
                            mt: "8px",
                            mb: "4px",
                        }}
                    >
                        <Select
                            value={dynamicParamType}
                            onChange={(e) =>
                                setDynamicParamType(e.target.value)
                            }
                        >
                            <MenuItem value="string">String</MenuItem>
                            <MenuItem value="boolean">Booleano</MenuItem>
                            <MenuItem value="integer">Inteiro</MenuItem>
                            <MenuItem value="float">Float</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="h6">Local*</Typography>

                    <FormControl
                        fullWidth
                        sx={{
                            mt: "8px",
                            mb: "4px",
                        }}
                    >
                        <Select
                            value={dynamicParamLocation}
                            onChange={(e) =>
                                setDynamicParamLocation(e.target.value)
                            }
                        >
                            <MenuItem value="header">Header</MenuItem>
                            <MenuItem value="path">URL</MenuItem>
                            <MenuItem value="query">Query</MenuItem>
                            <MenuItem value="json_body">JSON</MenuItem>
                            <MenuItem value="form_body">Form</MenuItem>
                        </Select>
                    </FormControl>

                    <FormGroup>
                        <FormControlLabel
                            label="Obrigatório"
                            sx={{ mt: 2 }}
                            control={
                                <Switch
                                    checked={dynamicParamRequired}
                                    onChange={(e) =>
                                        setDynamicParamRequired(
                                            e.target.checked,
                                        )
                                    }
                                />
                            }
                        />
                    </FormGroup>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Button
                            id="create-tool-dynamic-param-btn"
                            onClick={handleDynamicParamOk}
                            variant="contained"
                            sx={{
                                marginLeft: "auto",
                                marginRight: "auto",
                                marginTop: 2,
                            }}
                        >
                            OK
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default ToolsPage;
